import type { ReactElement } from 'react'

export interface LiveRegionProps {
  /** Empty string means "nothing to announce" — the container still renders. */
  readonly message: string
  readonly politeness?: 'polite' | 'assertive'
  readonly className?: string
}

/**
 * An announcement channel that assistive technology actually hears.
 *
 * The container renders **always**, even with nothing in it. A live region
 * inserted into the DOM at the moment of its announcement is frequently missed:
 * screen readers register the region when they encounter it, then watch it for
 * changes. An always-present region whose text changes is announced reliably; a
 * freshly-inserted one is a coin flip.
 *
 * The design rendered its share feedback into a bare `<span>` with no live
 * semantics at all, so "Link copiado." was visible and nothing else. That is the
 * defect this component exists to prevent.
 *
 * Its block size is reserved in CSS so an appearing message cannot shift layout —
 * the Lighthouse gate is `cumulative-layout-shift: 0`, exactly.
 */
export function LiveRegion({
  message,
  politeness = 'polite',
  className,
}: LiveRegionProps): ReactElement {
  return (
    <p
      className={['live-region', className ?? ''].join(' ').trimEnd()}
      aria-live={politeness}
      // Announce the region's full text, not just the changed node. With a single
      // text child the two agree, but `atomic` makes that explicit rather than
      // incidental.
      aria-atomic="true"
    >
      {message}
    </p>
  )
}
